/**
 * Денежная арифметика.
 *
 * Главное правило проекта: суммы НИКОГДА не хранятся и не считаются во float.
 * Внутри всё — целое число минорных единиц (копейки, центы, сатоши).
 * Наружу число превращается только при отображении и вводе.
 */

import type { Locale } from './enums';

/** Неразрывный пробел (U+00A0): «60 000 ₽» не должно разрываться переносом строки. */
export const NBSP = '\u00A0';

/**
 * Разные сборки ICU используют разные пробелы-разделители: узкий неразрывный
 * (U+202F), обычный неразрывный (U+00A0), тонкий (U+2009). Приводим к одному,
 * чтобы вывод был предсказуемым и в интерфейсе, и в тестах.
 */
const SPACE_VARIANTS = /[\u00A0\u202F\u2009\u2007\u2060]/g;

function normalizeSpaces(value: string): string {
  return value.replace(SPACE_VARIANTS, NBSP);
}

export interface CurrencyInfo {
  /** ISO-код или общепринятый тикер. */
  code: string;
  /** Количество знаков после запятой. */
  decimals: number;
  /** Символ для отображения. */
  symbol: string;
  /** Знак ставится до или после числа. */
  symbolPosition: 'before' | 'after';
  /** Криптовалюта — форматируется вручную, Intl её не знает. */
  crypto: boolean;
  nameRu: string;
  nameEn: string;
}

export const CURRENCIES: Record<string, CurrencyInfo> = {
  RUB: { code: 'RUB', decimals: 2, symbol: '₽', symbolPosition: 'after', crypto: false, nameRu: 'Российский рубль', nameEn: 'Russian ruble' },
  USD: { code: 'USD', decimals: 2, symbol: '$', symbolPosition: 'before', crypto: false, nameRu: 'Доллар США', nameEn: 'US dollar' },
  EUR: { code: 'EUR', decimals: 2, symbol: '€', symbolPosition: 'before', crypto: false, nameRu: 'Евро', nameEn: 'Euro' },
  KZT: { code: 'KZT', decimals: 2, symbol: '₸', symbolPosition: 'after', crypto: false, nameRu: 'Казахстанский тенге', nameEn: 'Kazakhstani tenge' },
  // Официальный знак сома U+20C0 есть далеко не во всех шрифтах, поэтому
  // используем словесное сокращение — оно читается везде одинаково.
  KGS: { code: 'KGS', decimals: 2, symbol: 'сом', symbolPosition: 'after', crypto: false, nameRu: 'Кыргызский сом', nameEn: 'Kyrgyzstani som' },
  UZS: { code: 'UZS', decimals: 2, symbol: 'сўм', symbolPosition: 'after', crypto: false, nameRu: 'Узбекский сум', nameEn: 'Uzbekistani sum' },
  AMD: { code: 'AMD', decimals: 2, symbol: '֏', symbolPosition: 'after', crypto: false, nameRu: 'Армянский драм', nameEn: 'Armenian dram' },
  AZN: { code: 'AZN', decimals: 2, symbol: '₼', symbolPosition: 'after', crypto: false, nameRu: 'Азербайджанский манат', nameEn: 'Azerbaijani manat' },
  UAH: { code: 'UAH', decimals: 2, symbol: '₴', symbolPosition: 'after', crypto: false, nameRu: 'Украинская гривна', nameEn: 'Ukrainian hryvnia' },
  BYN: { code: 'BYN', decimals: 2, symbol: 'Br', symbolPosition: 'after', crypto: false, nameRu: 'Белорусский рубль', nameEn: 'Belarusian ruble' },
  GEL: { code: 'GEL', decimals: 2, symbol: '₾', symbolPosition: 'after', crypto: false, nameRu: 'Грузинский лари', nameEn: 'Georgian lari' },
  USDT: { code: 'USDT', decimals: 2, symbol: 'USDT', symbolPosition: 'after', crypto: true, nameRu: 'Tether', nameEn: 'Tether' },
  USDC: { code: 'USDC', decimals: 2, symbol: 'USDC', symbolPosition: 'after', crypto: true, nameRu: 'USD Coin', nameEn: 'USD Coin' },
  BTC: { code: 'BTC', decimals: 8, symbol: '₿', symbolPosition: 'after', crypto: true, nameRu: 'Биткоин', nameEn: 'Bitcoin' },
  ETH: { code: 'ETH', decimals: 8, symbol: 'ETH', symbolPosition: 'after', crypto: true, nameRu: 'Эфириум', nameEn: 'Ethereum' },
  TON: { code: 'TON', decimals: 8, symbol: 'TON', symbolPosition: 'after', crypto: true, nameRu: 'Toncoin', nameEn: 'Toncoin' },
};

export const CURRENCY_CODES = Object.keys(CURRENCIES);

const FALLBACK_CURRENCY: CurrencyInfo = {
  code: 'XXX',
  decimals: 2,
  symbol: '',
  symbolPosition: 'after',
  crypto: false,
  nameRu: 'Валюта',
  nameEn: 'Currency',
};

export function getCurrency(code: string): CurrencyInfo {
  const key = (code ?? '').toUpperCase();
  return CURRENCIES[key] ?? { ...FALLBACK_CURRENCY, code: key || 'XXX', symbol: key };
}

export function isKnownCurrency(code: string): boolean {
  return Boolean(CURRENCIES[(code ?? '').toUpperCase()]);
}

/** Округление «половина от нуля»: 0.5 → 1, −0.5 → −1. Именно так считают деньги. */
export function roundHalfUp(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

function factorOf(currency: string): number {
  return 10 ** getCurrency(currency).decimals;
}

/**
 * Нормализует пользовательский ввод: убирает любые пробелы,
 * запятую превращает в точку.
 */
export function normalizeNumericString(input: string): string {
  return String(input)
    .replace(/[\s\u00A0\u202F\u2009\u2007]/g, '')
    .replace(/,/g, '.')
    .trim();
}

/** Человеческое число (123.45) → минорные единицы (12345). */
export function toMinor(amount: number | string, currency: string): number {
  const numeric = typeof amount === 'string' ? Number(normalizeNumericString(amount)) : amount;
  if (!Number.isFinite(numeric)) return 0;
  // Округляем сначала до разрядности валюты, чтобы 0.1 + 0.2 не дало 30.000000000000004
  return roundHalfUp(Number((numeric * factorOf(currency)).toFixed(4)));
}

/** Минорные единицы (12345) → человеческое число (123.45). */
export function fromMinor(minor: number, currency: string): number {
  return minor / factorOf(currency);
}

/** Умножение суммы на коэффициент (курс, доля) с корректным округлением. */
export function mulMinor(minor: number, multiplier: number): number {
  return roundHalfUp(minor * multiplier);
}

/** Процент от суммы: percentOfMinor(10000, 6) === 600. */
export function percentOfMinor(minor: number, percent: number): number {
  return roundHalfUp(Number(((minor * percent) / 100).toFixed(6)));
}

/**
 * Делит сумму на N частей без потери копеек.
 * splitMinor(100, 3) → [34, 33, 33]: сумма частей всегда равна исходной.
 */
export function splitMinor(minor: number, parts: number): number[] {
  if (parts <= 0) return [];
  const base = Math.trunc(minor / parts);
  let remainder = minor - base * parts;
  const step = remainder >= 0 ? 1 : -1;
  const result: number[] = [];
  for (let i = 0; i < parts; i += 1) {
    if (remainder !== 0) {
      result.push(base + step);
      remainder -= step;
    } else {
      result.push(base);
    }
  }
  return result;
}

/**
 * Делит сумму по весам без потери копеек (распределение призовых).
 * Остаток достаётся участникам с наибольшей дробной частью.
 */
export function splitMinorByWeights(minor: number, weights: number[]): number[] {
  const total = weights.reduce((sum, w) => sum + w, 0);
  if (total <= 0 || weights.length === 0) return weights.map(() => 0);

  const exact = weights.map((w) => (minor * w) / total);
  const floored = exact.map((v) => Math.trunc(v));
  let remainder = minor - floored.reduce((sum, v) => sum + v, 0);

  const order = exact
    .map((value, index) => ({ index, frac: value - Math.trunc(value) }))
    .sort((a, b) => b.frac - a.frac);

  let cursor = 0;
  while (remainder > 0 && order.length > 0) {
    const target = order[cursor % order.length]!;
    floored[target.index] = (floored[target.index] ?? 0) + 1;
    remainder -= 1;
    cursor += 1;
  }
  return floored;
}

/** Конвертация по курсу «сколько единиц целевой валюты за одну исходную». */
export function convertMinor(minor: number, from: string, to: string, rate: number): number {
  if ((from ?? '').toUpperCase() === (to ?? '').toUpperCase()) return minor;
  return toMinor(fromMinor(minor, from) * rate, to);
}

export interface FormatMoneyOptions {
  /** Показывать символ валюты. По умолчанию true. */
  withSymbol?: boolean;
  /** Всегда показывать знак (+/−) — полезно для дельт. */
  signDisplay?: 'auto' | 'always' | 'never';
  /** Скрывать нулевую дробную часть: «60 000 ₽» вместо «60 000,00 ₽». */
  compactDecimals?: boolean;
}

/**
 * Форматирование денег для отображения.
 * Для обычных валют используется Intl, для крипты символ подставляется вручную,
 * потому что Intl не знает тикеры BTC/USDT.
 */
export function formatMoney(
  minor: number,
  currency: string,
  locale: Locale = 'ru',
  options: FormatMoneyOptions = {},
): string {
  const info = getCurrency(currency);
  const { withSymbol = true, signDisplay = 'auto', compactDecimals = true } = options;

  const value = fromMinor(minor, currency);
  const hasFraction = Math.abs(minor % 10 ** info.decimals) > 0;
  const fractionDigits = compactDecimals && !hasFraction ? 0 : info.decimals;
  const intlLocale = locale === 'ru' ? 'ru-RU' : 'en-US';

  const numberPart = normalizeSpaces(
    new Intl.NumberFormat(intlLocale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      signDisplay:
        signDisplay === 'always' ? 'exceptZero' : signDisplay === 'never' ? 'never' : 'auto',
    }).format(value),
  );

  if (!withSymbol) return numberPart;

  return info.symbolPosition === 'before'
    ? `${info.symbol}${numberPart}`
    : `${numberPart}${NBSP}${info.symbol}`;
}

/** Короткий формат для графиков: «1,2 млн», «850 тыс.». */
export function formatMoneyCompact(minor: number, currency: string, locale: Locale = 'ru'): string {
  const info = getCurrency(currency);
  const value = fromMinor(minor, currency);
  const abs = Math.abs(value);
  const intlLocale = locale === 'ru' ? 'ru-RU' : 'en-US';

  let scaled = value;
  let suffix = '';
  if (abs >= 1_000_000) {
    scaled = value / 1_000_000;
    suffix = locale === 'ru' ? `${NBSP}млн` : 'M';
  } else if (abs >= 10_000) {
    scaled = value / 1_000;
    suffix = locale === 'ru' ? `${NBSP}тыс.` : 'K';
  }

  const numberPart = normalizeSpaces(
    new Intl.NumberFormat(intlLocale, { maximumFractionDigits: suffix ? 1 : 0 }).format(scaled),
  );

  return `${numberPart}${suffix}${NBSP}${info.symbol}`;
}

export function sumMinor(values: number[]): number {
  return values.reduce((acc, value) => acc + value, 0);
}

export function clampMinor(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
